import { Sha256 } from '@aws-crypto/sha256-js';
import { HttpRequest } from '@smithy/protocol-http';
import { SignatureV4 } from '@smithy/signature-v4';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useEffect, useRef, useState } from 'react';
import type { TypedUseSelectorHook } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import type { AppDispatch, RootState } from './store';

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export const useMobile = () => useMediaQuery({ query: '(max-width: 750px)' });

export const useSignedUrl = (url: string | undefined, shouldSign: boolean) => {
  const [signedUrl, setSignedUrl] = useState<URL | null>(null);

  useEffect(() => {
    if (!shouldSign) {
      setSignedUrl(url ? new URL(url) : null);
      return;
    }

    const updateSignedUrl = async () => {
      if (!url) return;

      const unsignedURL = new URL(url);

      // Skip signing if already signed or in test environment
      if (
        unsignedURL.searchParams.has('X-Amz-Algorithm') ||
        process.env.NODE_ENV === 'test'
      ) {
        setSignedUrl(unsignedURL);
        return;
      }

      const { credentials } = await fetchAuthSession();

      if (!credentials) {
        throw new Error('AWS credentials are undefined');
      }

      const region = process.env.REACT_APP_AWS_REGION as string;

      const request = new HttpRequest({
        hostname: unsignedURL.hostname,
        path: unsignedURL.pathname,
        method: 'GET',
        query: Object.fromEntries(unsignedURL.searchParams),
        headers: {
          host: unsignedURL.hostname,
        },
      });

      const signer = new SignatureV4({
        credentials,
        region,
        service: 'execute-api',
        sha256: Sha256,
      });

      // presign createss the SigV4 signature and adds it the to the query parameters of the request
      const signedRequest = await signer.presign(request);
      unsignedURL.search = new URLSearchParams(
        signedRequest.query as Record<string, string>
      ).toString();
      setSignedUrl(unsignedURL);
    };

    updateSignedUrl();
  }, [url, shouldSign]);
  return signedUrl;
};

// Allows setInterval to work (better) with react hooks
// using setInterval natively causes it to hold stale state
// https://overreacted.io/making-setinterval-declarative-with-react-hooks/
export const useInterval = (
  callback: () => void,
  delay: number,
  isPlaying: boolean = true
) => {
  const savedCallback = useRef<(() => void) | null>(null);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!isPlaying) return () => null;
    const tick = () => savedCallback.current?.();
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
    return () => null;
  }, [delay, isPlaying]);
};
